import { formatUnits } from 'viem';

import type { UserTrade } from '@/lib/api/speed';

/** Convert a raw token-decimal string (e.g. "15000000" for 15 USDC) to a number. */
export function tokenAmount(
    raw: string | null,
    decimals: number,
): number | null {
    if (raw === null) return null;
    try {
        return parseFloat(formatUnits(BigInt(raw), decimals));
    } catch {
        return null;
    }
}

/**
 * Parse a human-decimal string from /speed/trades.
 *
 * The endpoint mixes two encodings on the same object: `margin` /
 * `originalAmountIn` are USDC base units (use `tokenAmount`), while prices
 * (`entryPrice`, `closePrice`) AND the USD amounts (`pnl`, `openFee`,
 * `closeFee`, …) are plain decimals like "77610.0" / "0.051052". Feeding the
 * latter to `tokenAmount` makes `BigInt()` throw on the decimal point, which
 * its catch turns into a silent `null` — the "—" PnL bug.
 */
export function priceAmount(raw: string | null): number | null {
    if (raw === null) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
}

/**
 * Adaptive price label for a trade row: 2 decimals from $1 up, more as the
 * price shrinks. Returns null when the price is unknown so callers can render
 * their own placeholder.
 */
export function formatTradePrice(value: number | null): string | null {
    if (value === null) return null;
    const decimals = value >= 1 ? 2 : value >= 0.01 ? 4 : 6;
    return value.toFixed(decimals);
}

/**
 * /speed/trades carries no leverage field, so derive it from the filled
 * notional: `qty * entryPrice / margin`, rounded (Hyperliquid only accepts
 * integer leverage). Null for a row that never filled — a Failed or Pending
 * trade has no `qty` or `entryPrice`.
 */
export function leverageFromTrade(
    trade: Pick<UserTrade, 'qty'>,
    marginUsd: number | null,
    entry: number | null,
): number | null {
    if (trade.qty === null || marginUsd === null || !marginUsd || !entry) {
        return null;
    }
    const qty = Number(trade.qty);
    if (!Number.isFinite(qty)) return null;
    return Math.max(1, Math.round((qty * entry) / marginUsd));
}

/** Closed-trade outcomes that deserve a badge; a plain manual close gets none. */
export type TerminalStatus = Extract<
    UserTrade['status'],
    'TakeProfit' | 'StopLoss' | 'Liquidated' | 'Failed' | 'Cancelled'
>;

export const STATUS_META: Record<
    TerminalStatus,
    { label: string; className: string }
> = {
    TakeProfit: {
        label: 'TP',
        className: 'text-success',
    },
    StopLoss: {
        label: 'SL',
        className: 'text-destructive',
    },
    Liquidated: {
        label: 'LIQ',
        className: 'text-destructive',
    },
    Failed: {
        label: 'FAIL',
        className: 'text-muted-foreground',
    },
    // a limit order dropped before it ever filled: no position, no outcome to
    // colour, so it reads as muted as the FAIL badge beside it.
    Cancelled: {
        label: 'CANCELLED',
        className: 'text-muted-foreground',
    },
};

/** Badge for a LIVE resting limit order (a positions row, not a history one). */
export const RESTING_STATUS_META = {
    label: 'RESTING',
    className: 'text-muted-foreground',
} as const;

/**
 * Resolves the terminal badge (TP/SL/LIQ/FAIL) for a closed-history row.
 *
 * A trade that was closed by a trigger reports a terminal `status` directly
 * (e.g. `Liquidated`), but the backend may also settle it as `status: 'Closed'`
 * while recording the actual trigger in `closeReason` (e.g. TP/SL that routed
 * through a normal close). Prefer the explicit terminal `status`; otherwise
 * fall back to `closeReason` so the reason still surfaces on the row. A plain
 * manual close (`status: 'Closed'`, `closeReason` absent or `'Closed'`) yields
 * no badge — its outcome reads from the realized PnL.
 */
export function resolveTerminalStatus(
    trade: Pick<UserTrade, 'status' | 'closeReason'>,
): TerminalStatus | null {
    const candidate: UserTrade['status'] | null =
        trade.status === 'Closed' ? (trade.closeReason ?? null) : trade.status;
    if (
        candidate === 'TakeProfit' ||
        candidate === 'StopLoss' ||
        candidate === 'Liquidated' ||
        candidate === 'Failed' ||
        candidate === 'Cancelled'
    ) {
        return candidate;
    }
    return null;
}

/**
 * Resolve a trade's `pairBase` (a Hyperliquid coin, e.g. "BTC") to display info.
 * The base token symbol is lowercased for `TokenIcon`.
 */
export function resolvePair(
    pairBase: string,
): { label: string; baseToken: string } | null {
    if (!pairBase) return null;
    const coin = pairBase.toUpperCase();
    return {
        label: `${coin}/USD`,
        baseToken: coin.toLowerCase(),
    };
}
